// src/App.js - React App with authentication integration
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ApiStatusProvider } from './contexts/ApiStatusContext';
import ApiStatusBanner from './components/Common/ApiStatusBanner';
import Sidebar from './components/Sidebar';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import BookingsView from './components/BookingsView';
import CustomersView from './Views/CustomersView';
import PractitionersView from './components/PractitionersView';
import CalendarView from './Views/CalendarView';
import LocationsView from './Views/LocationsView';
import ServicesView from './Views/ServicesView';
import WidgetManagerView from './Views/WidgetManagerView';
import IntegrationsView from './Views/IntegrationsView';
import UserManagementView from './Views/UserManagementView';
import RolesPermissionsView from './Views/RolesPermissionsView';
import SettingsView from './Views/SettingsView';
import NotificationSystem, { useNotifications } from './components/Common/NotificationSystem';

// Import CSS
import './styles/App.css';
import './styles/Main.css';
import './styles/Calendar.css';
import './styles/SlideoutForms.css';
import './styles/Customers.css';
import './styles/Locations.css';
import './styles/Services.css';
import './styles/WidgetManager.css';
import './styles/Integrations.css';
import './styles/UserManagement.css';
import './styles/RolesPermissions.css';
import './styles/LoginScreen.css';

// App Content (inside Router + providers)
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { notifications, addNotification, removeNotification } = useNotifications();
  const { user, isAuthenticated, login, logout, isLoading, error } = useAuth();

  const handleSidebarCollapseChange = (isCollapsed) => {
    setSidebarCollapsed(isCollapsed);
  };

  const getCurrentSection = () => {
    const path = location.pathname.replace('/', '') || 'dashboard';
    return path;
  };

  const currentSection = getCurrentSection();

  const handleNavigation = (section) => {
    navigate(`/${section}`);
  };

  const showNotification = (message, type = 'success') => {
    addNotification(message, type);
  };

  const handleLoginSuccess = (result) => {
    if (result.success) {
      showNotification(`Welcome back, ${result.user?.email || 'User'}!`, 'success');
      navigate('/dashboard');
    }
  };

  const handleLogout = async () => {
    await logout();
    showNotification('Logged out successfully', 'info');
    navigate('/');
  };

  if (!isAuthenticated) {
    return (
      <>
        <ApiStatusBanner overlay />
        <LoginScreen
          onLogin={login}
          onLoginSuccess={handleLoginSuccess}
          isLoading={isLoading}
          error={error}
        />
      </>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar
        currentSection={currentSection}
        onNavigate={handleNavigation}
        onCollapseChange={handleSidebarCollapseChange}
        user={user}
        onLogout={handleLogout}
      />

      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* API unavailable banner — appears at the top of the content pane */}
        <ApiStatusBanner />

        <div className="content-area">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/calendar"
              element={
                <CalendarView showNotification={showNotification} />
              }
            />
            <Route path="/bookings" element={<BookingsView />} />
            <Route
              path="/practitioners"
              element={
                <PractitionersView showNotification={showNotification} />
              }
            />
            <Route
              path="/customers"
              element={
                <CustomersView showNotification={showNotification} />
              }
            />
            <Route
              path="/locations"
              element={
                <LocationsView showNotification={showNotification} />
              }
            />
            <Route
              path="/services"
              element={
                <ServicesView showNotification={showNotification} />
              }
            />
            <Route
              path="/widgets"
              element={
                <WidgetManagerView showNotification={showNotification} />
              }
            />
            <Route
              path="/integrations"
              element={
                <IntegrationsView showNotification={showNotification} />
              }
            />
            <Route
              path="/user-management"
              element={
                <UserManagementView showNotification={showNotification} />
              }
            />
            <Route
              path="/roles-permissions"
              element={
                <RolesPermissionsView showNotification={showNotification} />
              }
            />
            <Route
              path="/settings"
              element={
                <SettingsView showNotification={showNotification} />
              }
            />
          </Routes>
        </div>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
        floating={true}
      />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <ApiStatusProvider>
        <Router>
          <AppContent />
        </Router>
      </ApiStatusProvider>
    </AuthProvider>
  );
}

export default App;
